import type { TFunction } from 'i18next';

import { ApiHttpError } from '@/types';

/**
 * 點數域錯誤 → 畫面文案(單點;錯誤碼表 = Creative BE docs/141 §4)。
 * -----------------------------------------------------------------------------
 * 兩類講話方式(R343 / R350.8):
 *   - **使用者自己能處理的**(太頻繁 / 網路 / 工作區身分 / 方案剛更新 / 發票還沒開 / 已在退款中)只講人話與下一步。
 *   - **後端側的**(Mentor 沒回應 / Mentor 拒絕 / 認不得的碼)人話**外加 wire 原碼**(等寬顯示)——
 *     那串碼是把畫面接回 BE log 的唯一線索;「發生錯誤」一句話對所有失敗都一樣 = 不可診斷。
 */

/** 抽 wire 錯誤碼(BE 放 `error.code`;數字 429 之類照轉字串)。拿不到 = null。 */
export function pointsErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiHttpError)) return null;
  const code = err.error?.code;
  if (code === undefined || code === null || code === '') return null;
  return String(code);
}

/** `MENTOR_REJECTED` 的細分原因(`details.mentor_code`,Mentor 原樣碼);沒有 = null。 */
export function mentorRejectCode(err: unknown): string | null {
  if (pointsErrorCode(err) !== 'MENTOR_REJECTED' || !(err instanceof ApiHttpError)) return null;
  const details = err.error?.details;
  if (typeof details !== 'object' || details === null) return null;
  const mc = (details as { mentor_code?: unknown }).mentor_code;
  return typeof mc === 'string' && mc !== '' ? mc : null;
}

/**
 * 「這個環境沒有點數功能」(藏入口,不是錯誤):
 *   - 404 且不是業務碼(BE 這批端點沒部署:FastAPI JSON 404 → `HTTP_404`、邊緣層 HTML 404 → `NON_JSON_RESPONSE`)
 *   - 503 `MENTOR_NOT_CONFIGURED`(環境沒接 Mentor;BE 不設 feature flag,這是唯一的「關」)
 * ⚠️ 業務 404(`MENTOR_ORDER_NOT_FOUND`)與 403 工作區身分(`GROUP_FORBIDDEN` …)**不算** ——
 * 端點在,是這次請求的問題。
 */
export function isPointsUnavailableError(err: unknown): boolean {
  if (!(err instanceof ApiHttpError)) return false;
  const code = pointsErrorCode(err);
  if (err.status === 404) return code === null || code === 'HTTP_404' || code === 'NON_JSON_RESPONSE';
  if (err.status === 503) return code === 'MENTOR_NOT_CONFIGURED';
  return false;
}

export interface PointsErrorText {
  /** 人話。 */
  message: string;
  /** 要不要附 wire 原碼(後端側 / 認不得的錯才附)。 */
  code: string | null;
}

const WORKSPACE_CODES = new Set(['GROUP_FORBIDDEN', 'MEMBER_REMOVING', 'WORKSPACE_NOT_ACTIVE', 'CURRENT_GROUP_REQUIRED']);

/**
 * 通用錯誤描述。各情境專屬的碼(發票三碼 / 退款 409)由呼叫端先處理,認不得的再交給這支。
 */
export function describePointsError(err: unknown, t: TFunction<'points'>): PointsErrorText {
  if (err instanceof ApiHttpError) {
    const code = pointsErrorCode(err);
    if (err.status === 429) return { message: t('errors.rateLimited'), code: null };
    if (code === 'MENTOR_NOT_CONFIGURED') return { message: t('errors.MENTOR_NOT_CONFIGURED'), code };
    if (code === 'MENTOR_UPSTREAM_FAILED' || err.status === 502) {
      return { message: t('errors.MENTOR_UPSTREAM_FAILED'), code: code ?? `HTTP_${err.status}` };
    }
    if (code && WORKSPACE_CODES.has(code)) return { message: t('errors.workspace'), code: null };
    if (code === 'MENTOR_PACKAGE_NOT_FOUND') return { message: t('errors.MENTOR_PACKAGE_NOT_FOUND'), code: null };
    if (code === 'MENTOR_ORDER_NOT_PAYABLE') return { message: t('errors.MENTOR_ORDER_NOT_PAYABLE'), code: null };
    if (code === 'MENTOR_ORDER_CONFLICT') return { message: t('errors.MENTOR_ORDER_CONFLICT'), code };
    if (code === 'MENTOR_REJECTED') {
      const mc = mentorRejectCode(err);
      return { message: t('errors.MENTOR_REJECTED'), code: mc ? `MENTOR_REJECTED · ${mc}` : code };
    }
    return { message: t('errors.generic'), code: code ?? `HTTP_${err.status}` };
  }
  // 連回應都沒拿到(斷網 / DNS / 逾時)—— 使用者能處理:檢查網路再試。
  return { message: t('errors.network'), code: null };
}

/**
 * 付款頁連結拿不到 / 不可信(`checkPayUrl` 的兩種失敗)。兩者都是後端側 → 人話 + 原碼
 * (`PAY_URL_MISSING` = BE 回應沒有 `pay_url`;`PAY_URL_INVALID` = 有但不是可導去的網址)。
 */
export function payUrlErrorText(reason: 'missing' | 'invalid', t: TFunction<'points'>): PointsErrorText {
  return reason === 'missing'
    ? { message: t('errors.payUrlMissing'), code: 'PAY_URL_MISSING' }
    : { message: t('errors.payUrlInvalid'), code: 'PAY_URL_INVALID' };
}
